// The three machines (PLAN §3.2). Every piece is always its machine; there is
// no deploy state, because a piece that moves loses its connections on its own.

import {
  BISHOP,
  BISHOP_DIRS,
  CLASS_FODDER,
  EAST,
  NORTH,
  SOUTH,
  WEST,
  type Color,
  type Direction,
  fileOf,
  forwardFacing,
  KING,
  KING_DIRS,
  KNIGHT,
  makePiece,
  PAWN,
  pieceColor,
  type PieceType,
  pieceType,
  QUEEN,
  rankOf,
  ROOK,
  ROOK_DIRS,
} from "../board";
import {
  addKing,
  areEnemies,
  demandOf,
  facingAt,
  hasCell,
  moatLeft,
  noBuildingFor,
  pieceAt,
  pieceClassAt,
  promotesOn,
  setFacing,
  setPiece,
  setPromotionUses,
  type Position,
  variantAt
} from "../position";
import {
  DEMAND,
  HARVEST_RATE,
  MINTABLE,
  RULES,
  pieceVariant,
  SCAN_OFFSETS,
  DESIGN,
  type Grade,
  structureRow,
  inventoryOf
} from "../rules";
import {
  type Bill,
  billFor,
  COAL,
  FUEL,
  lineTop,
  METAL,
  needsFuel,
  refinableLine,
  SPECIAL,
  topMetal
} from "../materials";
import { materialLinks } from "./adjacency";
import { type CargoMove, laneDelivers } from "./belts";
import {
  createDemandCache,
  type DemandCache,
  type DemandField,
  demandField,
  hopsToDemand,
  laneIsLoadable,
  routeFrom,
  type RoutingRules
} from "./routing";
import {
  addHeap,
  addToBuffer,
  type Bank,
  beltFeeds,
  beltAcceptsFrom,
  beltIsLive,
  bufferAtLeast,
  bufferTotal,
  cargoTotal,
  depositAt,
  digProgressAt,
  DIR_OFFSET,
  liftDeposit,
  nextDepositUnit,
  handoffAt,
  heapAt,
  type IndustryState,
  cookingCount,
  dumpsAt,
  reserveAt,
  isCooking,
  kingActedAtOn,
  kingTaskAt,
  kingTempoAt,
  liveSquares,
  loadedAtOn,
  makeUnit,
  minedAtOn,
  pushUnit,
  refinerIdAt,
  removeUnit,
  pushCooking,
  setKingActedAt,
  kingBusyForForge,
  setKingTempo,
  acceptsGrade,
  deliverUnit,
  hasStructure,
  structureAt,
  setLoadedAt,
  setMinedAt,
  setRefinerId,
  shiftCooking,
  type Store,
  targetAt,
  unitAt,
  unitCount,
  mineableAt,
  setDigProgress,
  unitGrade,
  unitLine,
  unitMeets,
  unitRefinedBy,
  unitTouchedBy,
  unitValue,
  takeUnitOf,
  TASK_FORGE,
  TASK_REFINE,
  droppedAt,
  fuelAt,
  setFuel,
  takeFromGround
} from "./state";
import { isTurretKind, magazineRoom, turretTakes } from "./turret";
import { touches } from "./structures";
import { coalitionFor, fundableSquares, payCoalition, warehousesFor } from "./coalition";

/**
 * Refiners by rank (§31 M3): the king, the queen, and the bishop while
 * `DESIGN.bishopRefines`. A Confessor and a Miracle Healer refine by their
 * row (`refines`) whatever the switch says — ask `refinesAt` with a board.
 *
 * The king cooks too, and that is what retires the "a bishop must always be
 * mintable from grade-0 scrap" rule (PLAN §8 rev 4, rediscovered by four
 * consecutive design reviews). That rule existed for one reason: lose both
 * bishops and grade 1 becomes unreachable, but a bishop costs grade 1 — a
 * permanent economic deadlock. A king cannot be captured, so a refinery is now
 * on the board for as long as the game is, the deadlock is unreachable, and
 * the bishop can be priced like every other piece.
 */
export const isRefinery = (type: MachineType): boolean =>
  // The bishop's oven is a design switch (§31.1 Q1, `DESIGN.bishopRefines`).
  (type === BISHOP && DESIGN.bishopRefines) || type === KING || type === QUEEN;

/**
 * Does the piece on this square cook? Its rank's answer, or its variant row's
 * (`refines`) — and nothing for a row that traded the factory away.
 */
export const refinesAt = (position: Position, sq: number): boolean => {
  const type = machineType(position, sq);
  if (type === NO_MACHINE) return false;
  return isRefinery(type) || pieceVariant(variantAt(position, sq)).refines === true;
};

/** Does the piece on this square take material off the network? `isConsumer`, by square. */
export const consumesAt = (position: Position, sq: number): boolean =>
  refinesAt(position, sq) || machineType(position, sq) === ROOK;

/**
 * Is this unit fuel a refiner here would burn (§31 M2)? Coal, while metal
 * needs fuel. A refiner holds up to `RULES.refinerCoal` of it.
 */
/** A unit of coal, whatever the fuel switch says — what a generator holds (§31 S6). */
export const isCoal = (unit: number): boolean => unitLine(unit) === FUEL && unitGrade(unit) === COAL;

export const burnsAsFuel = (unit: number): boolean =>
  DESIGN.metalNeedsFuel && unitLine(unit) === FUEL && unitGrade(unit) === COAL;

/** Coal in this square's input bank. */
export function coalHeld(industry: IndustryState, sq: number): number {
  let held = 0;
  for (let i = 0; i < bufferTotal(industry, sq, "in"); i++) {
    if (burnsAsFuel(unitAt(industry, sq, i, "in"))) held++;
  }
  return held;
}

/** Does cooking this unit spend a fuel point (§31 M2)? */
const fuelled = (unit: number): boolean => needsFuel(unitLine(unit), DESIGN.metalNeedsFuel);

/** Metal in this oven's input bank that it could raise a rung — each wants a point. */
function metalAwaiting(position: Position, industry: IndustryState, sq: number): number {
  let waiting = 0;
  for (let i = 0; i < bufferTotal(industry, sq, "in"); i++) {
    const unit = unitAt(industry, sq, i, "in");
    if (fuelled(unit) && canRefine(position, industry, sq, unit)) waiting++;
  }
  return waiting;
}

/**
 * **Coal kept to match the metal** (2026-10-29): one per rung waiting, less
 * the points in the pool, never under `RULES.refinerCoal` — so coal can come
 * first and the metal after. A flat one starved an oven holding six iron.
 */
export function coalWanted(position: Position, industry: IndustryState, sq: number): number {
  return Math.max(RULES.refinerCoal, metalAwaiting(position, industry, sq) - fuelAt(industry, sq));
}

/**
 * Does this piece sit on what it holds?
 *
 * **A foundry stockpiles and everything else passes material along.** A
 * foundry is saving for a recipe, and the routing gradient would otherwise
 * walk its last two ingots off toward a refinery on the turn before you meant
 * to spend them; a line of pieces that each kept what it was handed is not a
 * bucket brigade.
 *
 * **`isFoundry`, so the king is one too.** This used to read `=== ROOK`, which
 * left the one piece that is a foundry *and* a refinery *and* a miner passing
 * its recipe savings down the network by default — the king is the machine the
 * economy is guaranteed to have, and it was the one machine that could not
 * save up.
 *
 * **`dump` is per unit, which is the whole point of replacing the old policy.**
 * That setting had a "pass on everything" state, so the switch that un-stuck a
 * full foundry was also the switch that stopped it ever filling: it walked the
 * recipe savings away an ingot at a time. Dumping keeps whatever this machine
 * can actually *use* — cook, or pay into the recipe it is saving for — and
 * pushes the rest back into the network. Asked without a unit it answers about
 * the piece, which is what the passes that choose a piece before they choose a
 * unit need.
 */
export function retainsMaterial(
  position: Position,
  industry: IndustryState,
  sq: number,
  unit?: number
): boolean {
  if (dumpsAt(industry, sq)) {
    return unit !== undefined && processesUnit(position, industry, sq, unit);
  }
  return isFoundryAt(position, industry, sq);
}

/**
 * Pieces that take material *off* the network: refineries and foundries.
 * Pawns and queens hold material too, but they feed lanes rather than eat
 * from them.
 */
/**
 * Who can forge. The king as well as the rook — it already refines and
 * harvests, and a piece you cannot lose being the one that can always build is
 * the same argument that made it a refinery: it guarantees the economy has a
 * way out of any hole, because the machine that provides it cannot be taken.
 */
export const isFoundry = (type: MachineType): boolean => type === ROOK || type === KING;

/**
 * **The question to ask about a square: can this forge?**
 *
 * `isFoundry` is a pure fact about a piece *type* and cannot see a Matriarch,
 * because being one is a row on one queen rather than a property of queens. So
 * this is the predicate every caller with a board in hand should use, and
 * `isFoundry` survives for the handful that genuinely only have a type to go
 * on.
 *
 * Deliberately *not* folded into `machineType`. That answers what a piece is
 * **by army style**, and its answer is a piece *kind* — filing a Matriarch as a
 * `ROOK` would hand her a rook's every other answer too. Two questions, two
 * functions, and this one composes them rather than duplicating either.
 *
 * `industry` is still taken, and no longer read: `foundry` is a row on the
 * position now. Left in place because every caller has one and the signature
 * is the seam between the two halves of this module.
 */
export const isFoundryAt = (
  position: Position,
  industry: IndustryState,
  sq: number
): boolean =>
  isFoundry(machineType(position, sq)) || foundryRow(position, sq);

/** Does this square's variant row forge, whatever its rank? See `PieceVariant`. */
const foundryRow = (position: Position, sq: number): boolean =>
  pieceVariant(variantAt(position, sq)).foundry === true;

export const isConsumer = (type: MachineType): boolean =>
  isRefinery(type) || type === ROOK;

/**
 * What a piece is *industrially* — its rank, unless its variant row traded
 * the factory away (`noMachine`).
 *
 * One place, rather than a variant test beside every `isRefinery` and
 * `isFoundry` in the codebase. The type predicates stay pure functions of a
 * piece type — which is what lets them be used from a badge, a planner and a
 * tick without any of them holding a position — and this is where a position
 * gets a say.
 *
 * `NO_MACHINE` is 0, which is not a piece type, so every predicate answers
 * false for it.
 */
export const NO_MACHINE = 0;
export type MachineType = PieceType | typeof NO_MACHINE;

export function machineType(position: Position, sq: number): MachineType {
  const piece = pieceAt(position, sq);
  if (!piece) return NO_MACHINE;
  const type = pieceType(piece);
  // **A turbo row traded the factory for reach**, filed here because four
  // predicates read machine-ness off this function.
  if (pieceVariant(variantAt(position, sq)).noMachine) return NO_MACHINE;
  return type;
}

/**
 * How much this piece digs a turn — the question `HARVEST_RATE` answers, asked
 * of a *square* so a variant row can have a say.
 *
 * A rate rather than a swap in `machineType`: `mineOnce` reads the rate off the
 * *piece type*, and `canHoldUnit` must keep answering "a pawn takes delivery
 * from nothing".
 */
export function harvestRate(position: Position, sq: number): number {
  const piece = pieceAt(position, sq);
  if (!piece) return 0;
  const type = pieceType(piece);
  if (machineType(position, sq) === NO_MACHINE) return 0;
  // **A variant's own rate, and `undefined` is the test rather than falsy.**
  // Zero is a real answer here — a Marshal has stopped digging on purpose, and
  // `?? HARVEST_RATE[type]` would read that as "no override" and hand it a
  // pawn's rate back.
  const row = pieceVariant(variantAt(position, sq)).harvest;
  if (row !== undefined) return row;
  return HARVEST_RATE[type];
}

/** True when the piece on this square works the ground at all. */
export const isHarvesterAt = (position: Position, sq: number): boolean =>
  harvestRate(position, sq) > 0;

/**
 * A piece standing on something somebody set down picks it up.
 *
 * One per arrival, into the input bank, while there is room. Not a mine:
 * mining is rate-limited and clamps to the heap.
 */
export function pickUpDropped(
  position: Position,
  industry: IndustryState,
  at: number
): void {
  if (droppedAt(industry, at).length === 0) return;
  const room = capacityAt(position, industry, at) - bufferTotal(industry, at, "in");
  if (room > 0) {
    pushUnit(industry, at, takeFromGround(industry, at), "in");
  }
}

/** How much a piece type will accept on its *input* side; 0 if it takes nothing. */
export function bufferCapacity(type: MachineType): number {
  switch (type) {
    case PAWN:
      return RULES.pawnBuffer;
    case BISHOP:
      return RULES.refineryInputBuffer;
    case KING:
      return RULES.kingBuffer;
    case ROOK:
      return RULES.foundryBuffer;
    case QUEEN:
      return RULES.queenBuffer;
    case KNIGHT:
      return RULES.carrierBuffer;
    default:
      return 0;
  }
}

/**
 * How much the piece on this square can hold — the question `bufferCapacity`
 * answers, asked of a *square* so a variant row can have a say.
 *
 * `bufferCapacity` survives for the question that really is about a *type* —
 * "could this kind of machine hold anything at all", which is capability rather
 * than capacity and is asked where there is no square to hand.
 */
export function capacityAt(
  position: Position,
  industry: IndustryState,
  sq: number
): number {
  const kind = machineType(position, sq);
  // **A crowned queen gets a foundry's hopper**, so a recipe can be saved
  // for. Equal to her own since every piece holds 20 (§33 I1); the floor
  // stays so the two cannot drift apart again.
  if (foundryRow(position, sq)) return Math.max(bufferCapacity(kind), RULES.foundryBuffer);
  return bufferCapacity(kind);
}

/** Whether a machine will take a delivery — used by belt flow. */
export function machineAccepts(
  position: Position,
  industry: IndustryState,
  square: number,
  owner: Color
): boolean {
  const piece = pieceAt(position, square);
  if (!piece || pieceColor(piece) !== owner) return false;
  const capacity = capacityAt(position, industry, square);
  return capacity > 0 && bufferTotal(industry, square, "in") < capacity;
}

/**
 * Miners. A pawn standing *on* a heap extracts, and pushes into the square it
 * faces — so facing is simultaneously where it moves, where it captures and
 * where its ore goes.
 */
/**
 * Which adjacent part a miner loads onto, or -1.
 *
 * Facing used to decide this: a pawn pushed into the one square it faced, and
 * aiming it was a whole knight move. That cost more turns than the ore was
 * worth, so a miner now finds its own belt. Facing still decides where a pawn
 * moves and captures — it just no longer gates the economy.
 *
 * Preference order, all of it deterministic because two clients derive state
 * independently from the same log (PLAN §5.5):
 *
 *  1. A part pointing *away* from the pawn. The pawn is then at its tail, so
 *     the load is carried off; a part pointing back at the pawn would just
 *     return the ore to the square it came from.
 *  2. Otherwise any of the pawn's own live parts.
 *  3. Ties break by ROOK_DIRS order, which is fixed.
 */
function loadingBelt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): number {
  // Two passes, and the first is the fix for ore poured into a spur while a
  // working lane sat on the miner's other side. A part that arrives nowhere is
  // still loaded when it is the only one there — a lane is built from its tail
  // and delivers nothing until its last part goes down, so refusing it would
  // stop anybody laying track ahead of the machine it is meant to feed.
  let fallback = -1;
  for (const adj of materialLinks(position, industry, sq)) {
    if (!beltAcceptsFrom((side) => areEnemies(position, owner, side), industry, adj)) continue;
    if (beltFeeds(position, industry, adj, sq)) continue;
    if (laneDelivers(position, industry, adj, owner)) return adj;
    if (fallback < 0) fallback = adj;
  }

  return fallback;
}

/**
 * Which square this piece digs — its own, or one it can reach. `-1` for none.
 *
 * **The one reading of "where does the ore come from"**, and both `canDig` and
 * `mineOnce` go through it, or a dig is legal against one square and drawn out
 * of another. Own square first, then clockwise from the one it faces.
 */
export function miningTargetAt(
  position: Position,
  industry: IndustryState,
  sq: number
): number {
  if (mineableAt(industry, sq) > 0) return sq;
  if (!pieceVariant(variantAt(position, sq)).minesAround) return -1;
  for (const offset of SCAN_OFFSETS[facingAt(position, sq)]) {
    const at = sq + offset;
    if (!hasCell(position, at) || mineableAt(industry, at) <= 0) continue;
    return at;
  }
  return -1;
}

/** True when this square is a piece of `owner`'s working ground it can reach. */
export const isMining = (
  position: Position,
  industry: IndustryState,
  sq: number
): boolean =>
  pieceAt(position, sq) !== 0 &&
  isHarvesterAt(position, sq) &&
  miningTargetAt(position, industry, sq) >= 0;

/** Why this piece cannot dig right now, or null. */
export function canMine(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
):
  | "not-a-harvester"
  | "no-scrap"
  | "buffer-full"
  | "already-mined"
  | "other-duty"
  | null {
  // Nothing left but the physical question. There used to be a second half —
  // a king posted to the ovens may not also dig — and it retired with the
  // king's shovel: `isHarvester` answers false for a king now, so `canDig`
  // refuses it before any of that arises. `"other-duty"` survives in the
  // return type because the UI still explains it for other reasons.
  return canDig(position, industry, sq, owner);
}

/**
 * Whether this piece could dig right now, ignoring what it has been told to
 * be doing. Everything except the king's one-job-a-turn rule.
 */
function canDig(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): "not-a-harvester" | "no-scrap" | "buffer-full" | "already-mined" | null {
  const piece = pieceAt(position, sq);
  if (!piece || pieceColor(piece) !== owner || !isHarvesterAt(position, sq)) {
    return "not-a-harvester";
  }
  // `miningTargetAt`, not `mineableAt`: the only thing that knows a DAM may be
  // looking next door, and `mineOnce` reads the same function.
  if (miningTargetAt(position, industry, sq) < 0) return "no-scrap";
  if (minedAtOn(industry, sq) === industry.turnCounter) return "already-mined";
  if (pieceType(piece) === KING && kingActedAtOn(industry, sq) === industry.turnCounter) {
    return "already-mined";
  }
  if (bufferTotal(industry, sq) >= capacityAt(position, industry, sq)) {
    return "buffer-full";
  }
  return null;
}

/**
 * One dig, once a turn. Whoever gets there first — the player or the tick.
 *
 * Clamped to what is actually in the ground and what the piece can actually
 * hold, or a rate above one would mine a heap of one into a heap of minus two
 * and conjure scrap out of the floor.
 *
 * **`sq` is the piece and `dig` is the ground**, the same square for everything
 * but a DAM: buffer, capacity, rate and the once-a-turn stamp stay on `sq`; the
 * deposit, the heap and a special's progress move to `dig`. Resolved here
 * rather than passed, so every caller is unchanged.
 */
export function mineOnce(
  position: Position,
  industry: IndustryState,
  sq: number
): number {
  const dig = miningTargetAt(position, industry, sq);
  if (dig < 0) return 0;
  const room = capacityAt(position, industry, sq) - bufferTotal(industry, sq);
  const rate = harvestRate(position, sq);

  // **The deposit first, the heap second.** Ore in the ground is what a field
  // is, and a corpse dropped on a tile buries it — so the two only ever
  // coexist for as long as it takes `wreckPiece` to run.
  const ore = depositAt(industry, dig);
  if (ore > 0) {
    // **A mixed tile lifts by its quota** (§31 D1, `nextDepositUnit`): the
    // named material or a cobble, decided by what is left, never a roll.
    const yielded = nextDepositUnit(industry, dig);

    // **A special is hunted, not strip-mined.** A dig attempt spends the turn
    // and lifts nothing until `specialDigTurns` of them have been spent, and
    // then exactly one unit comes up whatever the piece's rate is — scarcity
    // by time rather than by a number in a table.
    if (unitLine(yielded) === SPECIAL) {
      if (rate <= 0 || room < 1) return 0;
      setMinedAt(industry, sq, industry.turnCounter);
      // Progress is the tile's, the stamp the piece's — so two DAMs push one
      // special along together rather than each counting a shared hole.
      const turns = digProgressAt(industry, dig) + 1;
      if (turns < RULES.specialDigTurns) {
        setDigProgress(industry, dig, turns);
        return 0;
      }
      setDigProgress(industry, dig, 0);
      pushUnit(industry, sq, liftDeposit(industry, dig), "in");
      return 1;
    }

    // **A drill, not a shovel.** `RULES.depositDigRate` rather than the piece's
    // harvest rate: a tile is something a pawn occupies for six or ten turns,
    // feeding the lane beside it a unit a turn, and at a miner's two it was
    // gone in three. The rate still has to be *above nought* — that is what
    // asks whether this piece can dig at all.
    const cut = rate > 0 ? Math.min(RULES.depositDigRate, ore, room) : 0;
    if (cut <= 0) return 0;
    let lifted = 0;
    // One unit at a time, so a mixed tile's quota is asked per unit; a special
    // coming up next stops the drill — it is dug by time, next turn.
    while (lifted < cut && unitLine(nextDepositUnit(industry, dig)) !== SPECIAL) {
      const unit = liftDeposit(industry, dig);
      if (unit < 0) break;
      addToBuffer(industry, sq, unitGrade(unit) as Grade, 1, "in", unitLine(unit));
      lifted++;
    }
    if (lifted > 0) setMinedAt(industry, sq, industry.turnCounter);
    return lifted;
  }

  // No king branch any more: it used to book the turn against `TASK_MINE` and
  // multiply the rate by its tempo, and a king's harvest rate is zero now.
  // Heap off `dig`, ore into `sq` — the split the deposit branch above takes.
  const dug = Math.min(rate, heapAt(industry, dig), room);
  if (dug <= 0) return 0;

  addHeap(industry, dig, -(dug));
  addToBuffer(industry, sq, 0, dug);
  setMinedAt(industry, sq, industry.turnCounter);
  return dug;
}

/**
 * Whether this square would *ever* take this unit — the question with how full
 * it is left out of it.
 *
 * Split from `acceptsUnit` because the two are asked at different moments. The
 * routing field (`industry/routing.ts`) is built from this one and must not
 * depend on occupancy, or it would be stale before it was used and a full oven
 * would stop pulling the ore queueing for it. `acceptsUnit` is this plus room,
 * asked once, of the square material is about to be handed to.
 *
 * Each role has its own test —
 *
 *  - a refinery will not take something it has already processed (the same
 *    rule `takeRefinable` enforces when loading its oven), nor anything
 *    already at the top grade, since there is nowhere left to raise it to;
 *  - a foundry is a stockpile and takes anything, tested *before* the refinery
 *    rule because a king is both: a king can spend a diamond it cannot cook,
 *    and refusing it stranded the best material in the game at the one machine
 *    most able to use it;
 *  - a **pawn takes delivery from nothing but the ground**;
 *  - a carrier holds anything, being a courier rather than a consumer.
 */
export function canHoldUnit(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  unit: number
): boolean {
  // A building, before anything else: there is no piece on its square, so
  // every test below would refuse it out of hand.
  const structure = structureAt(industry, sq);
  if (structure) {
    if (areEnemies(position, owner, structure.owner)) return false;
    // **A turret takes its ammunition and nothing else** (§31 S8): marble
    // for a Kinetic, steel or better for a Cannon; a Laser takes none.
    // Capability, never occupancy — this builds the field.
    if (isTurretKind(structure.kind)) return turretTakes(structure, unit);
    // A Coal Generator burns coal and holds nothing else (§31 S6).
    if (structureRow(structure.kind).burnsCoal && !isCoal(unit)) return false;
    return acceptsGrade(structure, unitGrade(unit));
  }

  const piece = pieceAt(position, sq);

  // Not an *enemy*, rather than strictly yours. An allied army exists to feed
  // your economy, and one that could
  // only ever hand material to itself would need a second network to be any
  // use, which makes it a second economy rather than help with the first.
  if (!piece || areEnemies(position, owner, pieceColor(piece))) return false;

  const type = pieceType(piece);

  // **A pawn works the ground and takes delivery from nothing.**
  //
  // Its buffer is what it has dug, and the only thing that fills it is
  // `mineOnce`. A pawn is the bottom of the chain, not a link in it: one that
  // accepted a hand-off or a delivery off a lane was a place for material to
  // go *backwards* — down the grade ladder into the hands of the one piece
  // with no machine role, no oven and nowhere to spend it. Every route through
  // a pawn is a route to a dead end that looks alive.
  //
  // It still hands on everything it digs, which is the whole of its industrial
  // job, and it still stands on a heap and lifts it.
  if (type === PAWN) return false;

  if (capacityAt(position, industry, sq) === 0) return false;

  // Kings and rooks are the two pieces with somewhere to put material, so
  // they are the two with a hopper rather than an inbox — see
  // `RULES.foundryBuffer` and `RULES.kingBuffer`. What a machine will *hold*
  // has always been a separate question from what it will *process*, and
  // `takeRefinable` is still the one that decides the second.
  if (isFoundryAt(position, industry, sq)) return true;

  if (refinesAt(position, sq)) {
    // Coal too: it is what the oven burns to raise metal (§31 M2).
    return canRefine(position, industry, sq, unit) || burnsAsFuel(unit);
  }

  return true;
}

/** `canHoldUnit`, and there is room for it right now. */
export function acceptsUnit(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  unit: number
): boolean {
  if (!canHoldUnit(position, industry, sq, owner, unit)) return false;

  const structure = structureAt(industry, sq);
  if (structure) {
    // A turret's magazine is its inventory (§31 S8, §33 I2).
    if (isTurretKind(structure.kind)) return magazineRoom(structure) > 0;
    // Per *unit*, not per square, and this is the one machine where that
    // matters most: unlike a refinery — which hands on anything it cannot cook
    // and is therefore never durably full — a store stays full until something
    // takes material out of it. That is the point of it.
    // A generator's coal and an engine's tank too (§31 S6–S7): one cap,
    // the row's inventory (§33 I2).
    return structure.stock.length < inventoryOf(structure.kind);
  }

  // A refiner's coal is a fuel store, not a stockpile: capped at what its
  // metal needs, or a coal seam beside an oven fills its bank and it stops
  // taking ore.
  const held = bufferTotal(industry, sq, "in");
  const room = capacityAt(position, industry, sq);
  if (refinesAt(position, sq) && DESIGN.metalNeedsFuel) {
    if (burnsAsFuel(unit)) return held < room && coalHeld(industry, sq) < coalWanted(position, industry, sq);
    // **Metal keeps a slot for the coal it will burn** (2026-10-30): one per
    // rung, less coal held. Pool points are not counted — they expire at the
    // turn's start, and a bishop holding two iron on a lapsed pool had no slot
    // for the coal to cook either, for good. **Not a forger's**: a king saves
    // metal for a bill and spends it there, so its bank is never stuck.
    if (fuelled(unit) && !isFoundryAt(position, industry, sq) && canRefine(position, industry, sq, unit)) {
      const short = Math.max(0, metalAwaiting(position, industry, sq) + 1 - coalHeld(industry, sq));
      if (held + 1 + short > room) return false;
    }
    // **The last slot is the metal's** while fuel on hand outruns it: stone
    // there would leave the coal it took with nothing to burn for.
    if (
      !fuelled(unit) &&
      held + 1 >= room &&
      coalHeld(industry, sq) + fuelAt(industry, sq) > metalAwaiting(position, industry, sq)
    ) {
      return false;
    }
  }

  return held < room;
}

const isEnemyOf = (position: Position, owner: Color) => (side: Color) =>
  areEnemies(position, owner, side);

/**
 * The semantics of routing this one unit, for `industry/routing.ts` to search.
 *
 * `cache` present means the machines' demand is allowed to look one stage
 * ahead; absent means this *is* the lookahead and must not ask again.
 */
function routingRules(
  position: Position,
  industry: IndustryState,
  owner: Color,
  unit: number,
  cache?: DemandCache
): RoutingRules {
  return {
    demand: (sq) => demandAt(position, industry, sq, owner, unit, cache),
    canHold: (sq) => canHoldUnit(position, industry, sq, owner, unit),
    accepts: (sq) => acceptsUnit(position, industry, sq, owner, unit),
    // Only a consumer lifts material off a lane, and only its owner's — see
    // `grabFromAdjacent`. A knight beside a trunk line holds what it is handed
    // and can lift nothing.
    canGrab: (sq) =>
      consumesAt(position, sq) &&
      pieceColor(pieceAt(position, sq)) === owner &&
      canHoldUnit(position, industry, sq, owner, unit),
    ownsLane: (sq) => beltIsLive(industry, sq, owner),
    processes: (sq) => processesUnit(position, industry, sq, unit)
  };
}

/**
 * Would this machine *work* the unit, as against holding it?
 *
 * An oven that can raise its grade, or a forge saving for a recipe it pays
 * into. A stockpiling foundry with nothing set does not count — it is a place
 * to put material, which is a different thing from a stage on a line.
 */
function processesUnit(
  position: Position,
  industry: IndustryState,
  sq: number,
  unit: number
): boolean {
  if (refinesAt(position, sq) && (canRefine(position, industry, sq, unit) || burnsAsFuel(unit))) {
    return true;
  }
  if (!isFoundryAt(position, industry, sq)) return false;
  const target = targetAt(industry, sq) as PieceType | 0;
  return target !== 0 && billWants(position, target, unit);
}

/**
 * Does any row of this recipe's bill want this unit? The bill is the *gated*
 * one — a row whose line has no source yet is not being saved for — and grade
 * converts down within a line, so steel serves a recipe that asks for iron.
 */
function billWants(position: Position, target: PieceType, unit: number): boolean {
  for (const row of billFor(target)) {
    if (unitMeets(unit, row.line, row.grade)) return true;
  }
  return false;
}

/**
 * How badly this square wants this unit, as a head start on the routing field.
 *
 * The **min across every role the piece has**, which is the whole of what
 * retired `machinesOfKind`. That function classified a square as one kind or
 * the other with `isRefinery(type) ? "refiner" : isFoundry(type) ? "foundry"`,
 * and a king is both — so it was filed as a refiner and dropped out of the
 * foundry list for good. Measured consequence: on a board whose only foundry
 * was the king, top-grade material had **no destination at all**, and a
 * bishop's finished output sat in its out bank for the rest of the game with
 * the one piece that could spend it standing next to it.
 *
 * Asking each role separately and taking the best answer cannot fail that way,
 * because nothing has to decide what a king "is".
 */
function demandAt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  unit: number,
  /** Present when this machine may be charged for its own output's journey. */
  cache?: DemandCache
): number {
  if (!canHoldUnit(position, industry, sq, owner, unit)) return Infinity;
  // Behind the cheap `hasStructure` test — `demandAt` is the routing field's
  // hot path, and a board with no structures must pay nothing new.
  if (hasStructure(industry, sq)) {
    // Coal to a generator at the ovens' tier (§31 S6), as coal to a refiner;
    // a turret's ammunition the same (§31 S8) — a consumer, not a store.
    const kind = structureAt(industry, sq)!.kind;
    return structureRow(kind).burnsCoal || isTurretKind(kind)
      ? demandOf(position, owner, DEMAND.refiner)
      : RULES.demand.store;
  }

  let best = Infinity;
  // Coal goes to the ovens at the refiner's tier, and nowhere onward: it is
  // burned where it lands (§31 M2).
  if (burnsAsFuel(unit) && refinesAt(position, sq)) best = demandOf(position, owner, DEMAND.refiner);
  if (refinesAt(position, sq) && canRefine(position, industry, sq, unit)) {
    // **Charged for what it would then be holding.** An oven that raises a
    // grade and has nowhere to put the result is a worse place to send ore
    // than one standing beside a forge, and this is the whole of how the
    // stages end up in order between the source and whatever spends the metal.
    // A forge is the end of the line and pays nothing: what it makes is a
    // piece, which walks.
    best =
      demandOf(position, owner, DEMAND.refiner) +
      (cache ? onwardCost(position, industry, sq, owner, unit, cache) : 0);
  }
  if (isFoundryAt(position, industry, sq)) {
    const target = targetAt(industry, sq) as PieceType | 0;
    // Saving for something this unit can pay into, as against merely having a
    // hopper. A foundry with a recipe set is the closest thing in the game to a
    // machine that has *asked* for material.
    const want =
      target && billWants(position, target, unit)
        ? demandOf(position, owner, DEMAND.hungryFoundry)
        : demandOf(position, owner, DEMAND.foundry);
    if (want < best) best = want;
  }
  return best;
}

/**
 * How far the unit this oven would produce then has to travel, discounted.
 *
 * One level deep. The field it reads is built with the lookahead off, so a
 * chain of machines cannot ask each other this question forever and the cost is
 * one extra search per distinct thing a machine would make — which a side has
 * about as many of as it has ovens.
 */
function onwardCost(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  unit: number,
  cache: DemandCache
): number {
  const id = refinerIdAt(industry, sq);
  const top = lineTop(unitLine(unit));
  const made = unitRefinedBy(unit, id, Math.min(top, unitGrade(unit) + 1));

  // **This oven is not where its own output goes**, and saying so explicitly is
  // load-bearing rather than belt-and-braces. A refinery that has never cooked
  // has no id yet — ids are handed out lazily, from one — so `unitRefinedBy`
  // stamps nothing, the provenance mask that would have ruled this square out
  // is empty, and the oven answers "nought squares" to where its output has to
  // travel. Every fresh oven then looked perfectly connected and the whole
  // stage-ahead term quietly did nothing.
  const key = `${made}|${sq}`;
  let flat = cache.flat.get(key);
  if (!flat) {
    const rules = routingRules(position, industry, owner, made);
    flat = demandField(
      position,
      industry,
      isEnemyOf(position, owner),
      { ...rules, demand: (at) => (at === sq ? Infinity : rules.demand(at)) },
      // The same tick's memo: the lookahead field walks the same board and the
      // links do not depend on which unit is being routed.
      cache
    );
    cache.flat.set(key, flat);
  }

  const away = flat.cost.get(sq);
  if (away === undefined) return demandOf(position, owner, DEMAND.stranded);
  // **The cap stays strictly below `foundry` whatever a search asks for.** The
  // term compares *ovens*; let it reach the gap between the tiers and it stops
  // doing that and starts deciding whether ore is cooked at all. A rule, so a
  // tuned flow map cannot buy its way out of it.
  const cap = Math.min(
    demandOf(position, owner, DEMAND.onwardCap),
    demandOf(position, owner, DEMAND.foundry) - 1
  );
  return Math.min(cap, Math.round(away * demandOf(position, owner, DEMAND.onward)));
}

/**
 * The routing field for one unit: how far every square is from the machine
 * that wants this material most, over the network that actually exists.
 *
 * See `industry/routing.ts` for what this replaced and why.
 */
export function demandFor(
  position: Position,
  industry: IndustryState,
  owner: Color,
  unit: number,
  cache?: DemandCache
): DemandField {
  const cached = cache?.full.get(unit);
  if (cached) return cached;

  // A caller with nowhere to keep fields still gets the *same* field, on a
  // throwaway. The alternative — skipping the stage-ahead term when there is no
  // cache — would make routing depend on who was asking, so a preview in the
  // shelf could point at a square the tick does not.
  const jar = cache ?? createDemandCache();
  const field = demandField(
    position,
    industry,
    isEnemyOf(position, owner),
    routingRules(position, industry, owner, unit, jar),
    jar
  );

  jar.full.set(unit, field);
  return field;
}

/**
 * Every square that would take this unit as a *destination*, nearest first.
 *
 * Kept for the opponent, which asks it when deciding which way to point its own
 * lanes (`wantedRotations`). Reading the field's sources rather than a second
 * copy of the ranking, so the two can never disagree about what a sink is.
 */
export function sinksFor(
  position: Position,
  industry: IndustryState,
  owner: Color,
  unit: number,
  cache?: DemandCache
): number[] {
  const field = demandFor(position, industry, owner, unit, cache);
  const sinks: number[] = [];
  for (const [sq, own] of field.own) {
    if (field.cost.get(sq) === own) sinks.push(sq);
  }
  // Sorted, because a Map's order is agreement by luck and two clients derive
  // this independently (PLAN §5.5).
  return sinks.sort((a, b) => a - b);
}

/**
 * Hops from this square to the nearest machine of an **allied** army that
 * would take this unit, or -1 when the demand gradient never reaches one.
 *
 * **The economic flow between two sides, measured the way the reducer measures
 * every other flow.** What this replaced was a Chebyshev gap to the nearest
 * allied piece, which is the objection this module already answers for
 * material: straight-line distance measures *through* walls, gaps and armies,
 * and a straight line to something unreachable is not a route. A relay is a
 * hand-off chain like any other, so how far a load is from crossing is a
 * question about the network that exists — hand-off links, lanes, and the grabs
 * off them — and `hopsToDemand` is what already walks it.
 *
 * **One narrowing and nothing else.** The rules handed to the search are the
 * ordinary ones for this unit, so `canHold`, `canGrab`, `ownsLane` and the
 * edges are the reducer's own; the only change is that a *destination* has to
 * belong to somebody else in the alliance. Narrowed at `demand` rather than at
 * `canHold` deliberately: a square of ours that would hold the unit is still a
 * legitimate step on the way, and cutting it out of the graph would make an
 * allied machine two of our own pieces away read as unreachable.
 *
 * `-1` for no route, which is a hard zero at every call site rather than a
 * large distance. That is the same reading `demandField` gives an unreachable
 * machine — it contributes nothing to any square — and the whole reason a
 * gradient beats a ruler here.
 */
export function hopsToAlly(
  position: Position,
  industry: IndustryState,
  owner: Color,
  from: number,
  unit: number,
  limit: number
): number {
  const base = routingRules(position, industry, owner, unit);
  const rules: RoutingRules = {
    ...base,
    demand: (sq) => {
      const piece = pieceAt(position, sq);
      if (piece === 0) return Infinity;
      const side = pieceColor(piece);
      // Somebody else's, and on our side of the war. `areEnemies` answers
      // false for our own pieces too, which is why the first test is there:
      // material sitting in our own hands has not crossed anything.
      if (side === owner || areEnemies(position, owner, side)) return Infinity;
      return base.demand(sq);
    }
  };
  return hopsToDemand(position, industry, isEnemyOf(position, owner), rules, from, limit);
}

/**
 * Hops from this square to the nearest **forge of this side's**, or -1.
 *
 * Top-grade metal is the trick: nothing can raise a diamond, so no refinery is
 * ever a source for one, and what is left is exactly the foundries (and the
 * stores, which sit at a tier no board is wide enough to reach and so never
 * answer). One definition of downstream, shared with the routing the reducer
 * actually does.
 */
export function hopsToForge(
  position: Position,
  industry: IndustryState,
  owner: Color,
  from: number,
  limit: number
): number {
  // The top metal: nothing can raise it, so no refinery is ever a
  // source for it and what answers is exactly this side's forges.
  const unit = makeUnit(topMetal());
  return hopsToDemand(
    position,
    industry,
    isEnemyOf(position, owner),
    routingRules(position, industry, owner, unit),
    from,
    limit
  );
}

/**
 * Where a piece's next unit goes: downhill on the demand field, and a lane only
 * when nothing anywhere wants the material.
 *
 * `outbound` is a machine emptying its **out** bank. It is never its own
 * destination then, however much it might want a unit of that kind: the out
 * bank is what the machine has finished with, its oven loads from the *in*
 * bank, and a refinery that decided to keep its own output would stop.
 *
 * Returns the square and which store to push into.
 */
export function loadTarget(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  unit: number,
  cache?: DemandCache,
  outbound = false
): { square: number; machine: boolean } | null {
  // An explicit choice beats the field. The player can see a lane being built
  // or a foundry being saved for; the routing rule only sees the board as it
  // stands.
  const chosen = handoffAt(industry, sq);
  if (chosen) {
    const adj = sq + DIR_OFFSET[chosen - 1];
    if (hasCell(position, adj)) {
      const piece = pieceAt(position, adj);
      if (
        piece &&
        pieceColor(piece) === owner &&
        acceptsUnit(position, industry, adj, owner, unit)
      ) {
        return { square: adj, machine: true };
      }
      if (
        !piece &&
        beltAcceptsFrom((side) => areEnemies(position, owner, side), industry, adj)
      ) {
        return { square: adj, machine: false };
      }
    }
  }

  const field = demandFor(position, industry, owner, unit, cache);
  const isEnemy = isEnemyOf(position, owner);
  const route = routeFrom(
    position,
    industry,
    sq,
    routingRules(position, industry, owner, unit, cache),
    field,
    isEnemy,
    (lane) =>
      laneIsLoadable(industry, lane, sq, isEnemy) &&
      unitCount(industry, lane, "cargo") < RULES.beltCapacity,
    outbound
  );
  if (route) return route;

  // **The last resort is a lane, and only when nothing anywhere wants this.**
  //
  // This is what lets a player lay track ahead of the machine it is meant to
  // feed: a run is built from its tail and arrives nowhere until its last part
  // goes down, so a miner beside a half-built lane has to be able to prime it.
  // The field says nothing about such a lane — it leads to no destination, so
  // no square on it has a cost — which is exactly the condition to test.
  //
  // And **never merely because the route is busy**. A field that answers at all
  // means somebody wants the material, so a full oven means wait, not divert
  // onto the nearest track — which is how the old rule turned a stalled lane
  // into a scattering of ore across the network.
  //
  // An outbound bank is the exception on both counts: it has to empty whatever
  // the field says about the machine holding it.
  //
  // **And a store is the other exception, which is what a warehouse is for.**
  //
  // The rule above is right about a *lane*: a full oven means wait, because
  // diverting onto the nearest track is how a stalled lane scatters ore across
  // the network. It was wrong about the one destination somebody built,
  // paid for and pointed at exactly this case. Measured: a knight holding two
  // units, a stuffed oven on one side and an empty store on the other, held
  // them for the rest of the game while the warehouse beside it stayed empty
  // — the field wants the oven (tier 0 whatever its buffer says, because the
  // field is capability and not occupancy), so this returned null every tick.
  //
  // Deliberately *after* the descent, so it never competes with a machine that
  // can actually take the unit — the store's millionth-tier place in
  // `RULES.demand` is preserved exactly, and PLAN §14's rule that a store must
  // never outrank an oven with it.
  //
  // **It cannot shuttle**, which is the thing the tier was protecting. A store
  // reached this way is a source next tick (`runStores` runs first) and hands
  // on toward a real consumer; and a store never falls back to another store,
  // because the source here is a piece. So the worst case is a warehouse
  // acting as a buffer in front of a busy oven, which is the job.
  if (field.cost.has(sq) && !outbound) {
    const stored = storeBeside(position, industry, sq, owner, unit);
    return stored >= 0 ? { square: stored, machine: true } : null;
  }

  const belt = loadingBelt(position, industry, sq, owner);
  if (
    belt >= 0 &&
    unitCount(industry, belt, "cargo") < RULES.beltCapacity
  ) {
    return { square: belt, machine: false };
  }
  return null;
}

/**
 * A store of ours next to `sq` that would take this unit right now, or -1.
 *
 * `acceptsUnit` per unit rather than per square, because "full" is *durable*
 * for a store in a way it is not for a refinery: an oven empties itself every
 * tick and a warehouse does not, so asking about the square would send
 * material to one that has been full since the middle of the last front.
 *
 * Never from a structure: a store handing to a store is the shuttle this whole
 * arrangement is built to avoid.
 */
function storeBeside(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  unit: number
): number {
  if (hasStructure(industry, sq)) return -1;
  for (const adj of materialLinks(position, industry, sq)) {
    const structure = structureAt(industry, adj);
    if (!structure || structure.owner !== owner) continue;
    if (acceptsUnit(position, industry, adj, owner, unit)) return adj;
  }
  return -1;
}

/**
 * The first unit this piece could actually hand on, and where it would go.
 *
 * **Not blindly unit zero**, which is what every caller used to read. A unit
 * with nowhere to go is not a reason for the rest of the bank to stay put: a
 * stuck unit at the head of the queue used to plug the ore behind it, on the
 * lane, in the brigade and in the CTA that offers the hand-off.
 *
 * Skipping, not reordering: the bank keeps its order and "take the oldest
 * thing that can move" is still cheapest-first among the ones that can, so
 * nothing about the spend rule changes.
 *
 * `wanted` narrows it for the brigade, which only ever hands to machines.
 */
function nextHandoff(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  wanted?: (target: { square: number; machine: boolean }) => boolean,
  cache?: DemandCache
): { index: number; target: { square: number; machine: boolean } } | null {
  for (let i = 0; i < unitCount(industry, sq, "in"); i++) {
    const target = loadTarget(position, industry, sq, owner, unitAt(industry, sq, i, "in"), cache);
    if (target && (!wanted || wanted(target))) return { index: i, target };
  }
  return null;
}

/**
 * End of turn: every piece holding material passes one unit along the gradient
 * to a piece it is touching.
 *
 * This is what makes a line of pieces a bucket brigade — material walks from
 * wherever it was picked up to whatever can use it, without needing a belt
 * laid between two things that are already standing next to each other.
 */
/** The carriers' hand-off pass. */
export function transferBetweenPieces(
  position: Position,
  industry: IndustryState,
  owner: Color,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[],
  /** Optional: this tick's routing fields, so each is built once. */
  cache?: DemandCache
): void {
  for (const sq of liveSquares(position.board)) {
    transferFrom(position, industry, sq, owner, moves, cache);
  }
}

/** One square's carry hand-off. True when anything actually moved. */
function transferFrom(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  moves?: CargoMove[],
  cache?: DemandCache
): boolean {
  const piece = pieceAt(position, sq);
  if (pieceColor(piece) !== owner) return false;
  const type = machineType(position, sq);

    // A refinery hands off in `runMachineHandoffs`, which knows about its two
    // banks and about material it cannot cook.
    if (refinesAt(position, sq)) return false;

    // A rook reaches here now rather than being skipped outright. It still
    // keeps everything by default — that is what `retainsMaterial` says about
    // a foundry — but "default" is a default rather than a law, so a foundry
    // holding material it can no longer use is no longer a dead end.
    if (retainsMaterial(position, industry, sq)) return false;

    // Kings and queens work a chain twice as fast as anything else. They are
    // the pieces with the range to be somewhere useful, and at one unit a turn
    // a brigade through either was slower than the belt beside it.
    const rate = type === KING || type === QUEEN ? RULES.royalHandoffRate : 1;

    let sent = 0;
    for (let moved = 0; moved < rate; moved++) {
      // **The reserve is a floor on the automatic pass, not a lock.**
      //
      // A piece holds back N units and passes the rest along — which is what
      // lets it sit in the middle of a working lane and still keep a stock the
      // carry gradient would otherwise drain. Default zero, which is exactly the old behaviour: a piece
      // nobody has configured passes everything on.
      //
      // The automatic pass only. An explicit hand-off still empties it,
      // exactly as `hold` already works — two rules that behave differently
      // for the same intent is how a player ends up with two mental models.
      if (bufferTotal(industry, sq, "in") <= reserveAt(industry, sq)) break;

      // Route the *specific* unit we would send: whether a machine is a valid
      // destination depends on the unit's grade and on who has already cooked
      // it. The first one that routes *anywhere*, not whichever is at the
      // front — a stuck unit at the head of the bank used to stop the brigade.
      const sending = nextHandoff(position, industry, sq, owner, (t) => t.machine, cache);
      if (!sending) break;
      const target = sending.target;

      // Hand over what the destination wants most, not simply the oldest thing
      // we are holding — a carrier standing beside a foundry that needs ore
      // should offer it the ore rather than the steel behind it in the queue.
      // Falling back to the unit we routed on, which is known to have a home.
      const wanted = bestUnitFor(position, industry, target.square, owner, sq, "in");
      const index = wanted >= 0 ? wanted : sending.index;
      const unit = removeUnit(industry, sq, index, "in");
      if (!deliverUnit(position, industry, target.square, unit)) {
        pushUnit(industry, sq, unit, "in");
        break;
      }
      moves?.push({
        from: sq,
        fromIndex: -1,
        to: target.square,
        toIndex: -1,
        phase: "handoff"
      });
      sent++;
    }
  return sent > 0;
}

/** Why a pawn cannot put its load down right now, or null. */
export function canLoadBelt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): "not-your-pawn" | "nothing-held" | "nowhere-to-put-it" | "already-loaded" | null {
  const piece = pieceAt(position, sq);
  if (!piece || pieceColor(piece) !== owner || pieceType(piece) !== PAWN) {
    return "not-your-pawn";
  }
  if (bufferTotal(industry, sq) === 0) return "nothing-held";
  if (loadedAtOn(industry, sq) === industry.turnCounter) return "already-loaded";
  // Any unit that could go, not whichever happens to be at the front.
  if (!nextHandoff(position, industry, sq, owner)) return "nowhere-to-put-it";
  return null;
}

/** One hand-off a turn: a unit from the pawn onto a belt or into a machine. */
/**
 * How many units a piece may put on a lane in one turn.
 *
 * **A miner passes on what it digs.** The rate here was a flat one, which was
 * right while a dig was also one — the two numbers agreed and nobody had to
 * say so. Doubling `minerRate` broke that silently and in the worst possible
 * way: a pawn digs two into a two-unit buffer, hands one on, and has no room
 * to dig next turn, so the *net* throughput of a pawn on a heap feeding a lane
 * would have stayed exactly where it was. The bottleneck would have moved from
 * the pit to the pawn's own hands, and the tuning change would have read as
 * having done nothing.
 *
 * Kings and queens keep `royalHandoffRate`, which is their rule everywhere
 * else material moves between pieces — a king digs far faster than it passes,
 * and that is fine, because a king is a consumer: it refines and forges out of
 * the same hopper.
 */
export function handoffRate(type: MachineType): number {
  if (type === KING || type === QUEEN) return RULES.royalHandoffRate;
  return Math.max(1, HARVEST_RATE[type as PieceType] ?? 0);
}

export function loadBeltOnce(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[],
  cache?: DemandCache
): number {
  // **Nothing to send is not a hand-off.** This used to be guaranteed by the
  // caller — `canLoadBelt` refuses an empty piece and was the only way in — and
  // that stopped being true the moment a miner could hand on more than one unit
  // a turn: the second pass read unit zero of an empty bank, got nothing, and
  // pushed it onto the lane anyway. A unit out of an empty buffer is *material
  // created*, which is the one thing a tick may never do.
  if (unitCount(industry, sq, "in") === 0) return -1;
  const sending = nextHandoff(position, industry, sq, owner, undefined, cache);
  if (!sending) return -1;
  const target = sending.target;
  const unit = removeUnit(industry, sq, sending.index, "in");
  // A machine target may be a *building*, which keeps its material somewhere
  // else entirely — `deliverUnit` is the one place that knows which.
  //
  // The slot is read *before* the push, because that is where it lands — the
  // same reason `advanceBelts` reads it there.
  const slot = target.machine ? -1 : cargoTotal(industry, target.square);
  const landed = target.machine
    ? deliverUnit(position, industry, target.square, unit)
    : pushUnit(industry, target.square, unit, "cargo");
  // Put back, never dropped: a full part (§33 I3) refuses the push.
  if (!landed) {
    pushUnit(industry, sq, unit, "in");
    return -1;
  }
  moves?.push({
    from: sq,
    fromIndex: -1,
    to: target.square,
    toIndex: slot,
    phase: "handoff"
  });
  setLoadedAt(industry, sq, industry.turnCounter);
  return target.square;
}

/**
 * Mining and loading are player actions now, not tick steps, so this reports
 * nothing. Kept as the seam the Gambit uses: it runs the economy to
 * exhaustion, and there is no player there to press anything, so it digs and
 * loads on everyone's behalf.
 */
/**
 * Squares holding fodder this side's commanding variants stand beside.
 *
 * **Own fodder only, and `PIECE_CLASS` is the fence** — the first rule keyed by
 * it, which is what the table was for. Never the commander itself. Sorted,
 * because it decides who digs when a heap runs out mid-pass.
 */
export function commandedFodder(position: Position, owner: Color): number[] {
  const out: number[] = [];
  if (position.variant.size === 0) return out;
  const seen = new Set<number>();
  for (const at of liveSquares(position.board)) {
    const piece = pieceAt(position, at);
    if (pieceColor(piece) !== owner) continue;
    if (!pieceVariant(variantAt(position, at)).commandsFodder) continue;
    for (const offset of KING_DIRS) {
      const sq = at + offset;
      if (seen.has(sq) || !hasCell(position, sq)) continue;
      const neighbour = pieceAt(position, sq);
      if (!neighbour || pieceColor(neighbour) !== owner) continue;
      // **`pieceClassAt`, not `pieceClass`** — a row may disagree with its
      // rank now (a Matriarch is a Monarch), and one of the two answers has to
      // be the one every fence reads.
      if (pieceClassAt(position, sq) !== CLASS_FODDER) continue;
      seen.add(sq);
      out.push(sq);
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * One more go at something a square may do once a turn.
 *
 * **The stamp *is* the once-a-turn rule**, so this is a second stamp — put back
 * when the go is not taken, or the `mine` button hands out a third dig.
 */
function withBonusTick(
  industry: IndustryState,
  sq: number,
  stampOf: (industry: IndustryState, sq: number) => number,
  stamp: (industry: IndustryState, sq: number, turn: number) => void,
  act: () => boolean
): void {
  const was = stampOf(industry, sq);
  stamp(industry, sq, -1);
  if (!act()) stamp(industry, sq, was);
}

export function runMining(position: Position, industry: IndustryState, owner: Color): number {
  let mined = 0;

  for (const sq of liveSquares(position.board, industry.heap, industry.deposit)) {
    if (canMine(position, industry, sq, owner) === null) {
      mined += mineOnce(position, industry, sq);
    }
  }

  return mined;
}

/**
 * The Marshal's bonus: one more dig **and** one more hand-off for adjacent
 * fodder. A whole second miner's turn, not a second dig.
 *
 * **After the hand-off pass, and the ordering is the mechanic**: inside
 * `runMining` the pawn is already full and the bonus is refused `buffer-full`.
 * **Not a §26 F4 tick** — nothing here advances `turnCounter`.
 */
export function runCommandedTicks(
  position: Position,
  industry: IndustryState,
  owner: Color,
  moves?: CargoMove[],
  cache?: DemandCache
): number {
  let mined = 0;
  for (const sq of commandedFodder(position, owner)) {
    withBonusTick(industry, sq, minedAtOn, setMinedAt, () => {
      if (canMine(position, industry, sq, owner) !== null) return false;
      mined += mineOnce(position, industry, sq);
      return true;
    });
    // A retained pawn keeps its ore, exactly as it does in the ordinary pass —
    // the policy is about what happens when nobody is pressing anything.
    if (retainsMaterial(position, industry, sq)) continue;
    withBonusTick(industry, sq, loadedAtOn, setLoadedAt, () => {
      if (canLoadBelt(position, industry, sq, owner) !== null) return false;
      loadBelt(position, industry, sq, owner, moves, cache);
      return true;
    });
  }
  return mined;
}

/** Miners put a unit down on a lane. The hand-off half of a miner's turn. */
export function runMinerHandoffs(
  position: Position,
  industry: IndustryState,
  owner: Color,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[],
  /** Optional: this tick's routing fields, so each is built once. */
  cache?: DemandCache
): void {
  for (const sq of liveSquares(position.board, industry.heap, industry.deposit)) {
    // A retained pawn keeps its ore. The player's own hand-off still works —
    // this is the automatic pass, and the policy is about what happens when
    // nobody is pressing anything.
    if (retainsMaterial(position, industry, sq)) continue;
    if (canLoadBelt(position, industry, sq, owner) === null) {
      loadBelt(position, industry, sq, owner, moves, cache);
    }
  }
}

/**
 * A turn's worth of hand-off onto a lane: up to `handoffRate` units.
 *
 * The target is re-chosen per unit rather than once for the batch — a lane
 * square that has just taken one may be the wrong answer for the next, and
 * `loadTarget` already prefers a part pointing away from the miner.
 * Stops early when there is nothing left to give or nowhere to put it.
 */
export function loadBelt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[],
  cache?: DemandCache
): number {
  let moved = 0;
  const rate = handoffRate(machineType(position, sq));
  for (let i = 0; i < rate; i++) {
    if (loadBeltOnce(position, industry, sq, owner, moves, cache) < 0) break;
    moved++;
  }
  return moved;
}

/**
 * Refineries. Quality, not quantity: a bishop raises material one grade and
 * charges you a turn and a committed piece for it, never tonnage.
 *
 * Order matters (PLAN §4's second trap): emit, then finish the oven, then
 * start the next batch. Starting before finishing would let a unit that
 * arrived this tick skip the one-turn cook entirely.
 */
/**
 * This refinery's stable identity, assigned on first use.
 *
 * Lazy rather than at game start because pieces are minted mid-game and a FEN
 * can start with any number of them; the counter lives in state so a replay
 * hands out exactly the same ids in exactly the same order.
 */
function refinerIdFor(industry: IndustryState, sq: number): number {
  if (refinerIdAt(industry, sq) === 0) {
    setRefinerId(industry, sq, industry.nextRefinerId);
    industry.nextRefinerId++;
  }
  return refinerIdAt(industry, sq);
}

/**
 * The index of the **highest**-grade unit this refinery may still work on.
 *
 * Dearest first. A refinery's whole output is one grade per unit per turn, so
 * the question is which unit that turn is spent on — and spending it on the
 * silver rather than the iron is worth strictly more, because cooking adds a
 * grade wherever it lands and a grade is worth more the higher it starts.
 *
 * It was cheapest-first, on the reasoning that governs *spending*: burn the
 * raw stuff and keep the refined. That is right for a foundry paying a recipe
 * and backwards for an oven — a refinery does not consume value, it adds it,
 * and starving the good material to cook scrap leaves a bank of half-finished
 * ore that never becomes anything.
 *
 * It cannot starve the scrap either, which is what makes this safe: a refinery
 * may never touch the same unit twice and refuses anything already at the top
 * grade, so the pool of better material drains and the iron comes up next.
 */
function refinableIndex(
  position: Position,
  industry: IndustryState,
  sq: number,
  id: number,
  /** Which units are cookable now — the fuel gate (§31 M2). All, by default. */
  allow: (unit: number) => boolean = () => true
): number {
  let bestIndex = -1;
  let bestValue = -Infinity;
  for (let i = 0; i < bufferTotal(industry, sq, "in"); i++) {
    const unit = unitAt(industry, sq, i, "in");
    if (unitTouchedBy(unit, id)) continue;
    if (!allow(unit)) continue;
    if (!refinableLine(unitLine(unit))) continue;
    if (unitGrade(unit) >= lineTop(unitLine(unit))) continue;
    // Dearest by the cross-line price, since "worth more the higher it
    // starts" needs one answer for a bank holding stone beside steel.
    // Strictly better, so ties keep the oldest unit — a fixed order, because
    // two clients derive this independently (PLAN §5.5).
    const value = unitValue(unit);
    if (value > bestValue) {
      bestValue = value;
      bestIndex = i;
    }
  }
  return bestIndex;
}

/**
 * Could this refinery put this unit in its oven? Not "would it hold it".
 *
 * Either line, one step a turn, up to the **line's top**. The special line is
 * never refined: grade there is identity, not progress, and electrum and
 * diamond only ever come out of the ground.
 */
export function canRefine(
  position: Position,
  industry: IndustryState,
  sq: number,
  unit: number
): boolean {
  const line = unitLine(unit);
  if (!refinableLine(line)) return false;
  return (
    unitGrade(unit) < lineTop(line) &&
    !unitTouchedBy(unit, refinerIdAt(industry, sq))
  );
}

/**
 * Does the refiner here want a fuel point this tick (§31 S6's consumer test)?
 * Its pool is empty and the dearest unit it would load is metal, which needs
 * one. The network fills it before it would burn its own coal.
 */
export function wantsFuel(position: Position, industry: IndustryState, sq: number): boolean {
  if (!DESIGN.metalNeedsFuel || fuelAt(industry, sq) > 0 || !refinesAt(position, sq)) return false;
  // Any metal it could raise, not only when metal is the dearest unit: the
  // oven cooks metal first once it has the point (`runRefineries`).
  return refinableIndex(position, industry, sq, refinerIdFor(industry, sq), (unit) => needsFuel(unitLine(unit), true)) >= 0;
}

/**
 * Could this refiner fuel a cook of `unit` — a point in hand, or coal to burn
 * for one (§31 M2)? Stone and a fuel-free design always can.
 */
export const fuelable = (industry: IndustryState, sq: number) => (unit: number): boolean =>
  !needsFuel(unitLine(unit), DESIGN.metalNeedsFuel) ||
  fuelAt(industry, sq) > 0 ||
  coalHeld(industry, sq) > 0;

/**
 * Everything a machine pushes *out*, in one pass.
 *
 * Two jobs, and the second is the one that was missing. A refinery emits its
 * finished material toward a foundry or onto a lane — it always did. But a
 * unit it *cannot* process — one it has already refined, or one already at the
 * top grade — used to sit in its input bank for the rest of the game, because
 * `takeRefinable` skips exactly those and nothing else ever looked at them. A
 * refinery standing between two lanes would fill up and stop.
 *
 * So a refinery now hands on anything it cannot use, to whatever will take it:
 * `loadTarget` already ranks a valid machine above a belt, and `acceptsUnit`
 * already knows this refinery is not a valid destination for a unit it has
 * touched. The material keeps moving instead of dying where it stopped being
 * useful.
 */
export function runMachineHandoffs(
  position: Position,
  industry: IndustryState,
  owner: Color,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[],
  /** Optional: this tick's routing fields, so each is built once. */
  cache?: DemandCache
): void {
  for (const sq of liveSquares(position.board)) {
    const piece = pieceAt(position, sq);
    if (pieceColor(piece) !== owner) continue;
    const type = machineType(position, sq);
    if (!refinesAt(position, sq)) continue;
    // **No retain gate here, and that is deliberate.** An oven's *output* has
    // to keep moving whatever the piece has been told: a king is oven and
    // foundry both, and gating this on "can it use the unit" fills the two-slot
    // out bank, stops the oven emptying, and a lone king on a heap does nothing
    // at all. Measured — it takes the headless campaign from clearing fronts to
    // clearing none. The block below is what lets a king keep its own output:
    // it takes it back *in hand*, which empties the bank rather than blocking
    // it. `dump` is about the input side, where hoarding is the failure.
    const id = refinerIdFor(industry, sq);

    // Finished material first: it is what the foundry downstream is waiting on.
    //
    // Routed like everything else now. It used to be aimed at
    // `machinesOfKind(..., "foundry")` — rooks and nothing else, since a king
    // was classified a refiner — so a bishop's output on a board with no rook
    // was pushed onto whatever lane happened to be adjacent, or parked in the
    // out bank for good.
    const finished = unitCount(industry, sq, "out") > 0 ? unitAt(industry, sq, 0, "out") : -1;
    if (finished >= 0) sendOne(position, industry, sq, owner, "out", finished, 0, moves, cache);

    // A king takes its own refined material back in hand.
    //
    // It is the one piece that is both oven and foundry, so its output is
    // something *it* can spend — and the output bank is two slots. A lone king
    // refined two units, filled it, and jammed: the oven could not empty, so
    // it stopped cooking, so a king standing on a heap with a full input bank
    // did nothing at all. It cannot re-cook what it takes back, because
    // `takeRefinable` skips anything this refinery has already touched, which
    // is exactly what the provenance mask is for.
    if (type === KING) {
      while (unitCount(industry, sq, "out") > 0) {
        const unit = unitAt(industry, sq, 0, "out");
        if (bufferTotal(industry, sq, "in") >= bufferCapacity(type)) break;
        removeUnit(industry, sq, 0, "out");
        pushUnit(industry, sq, unit, "in");
      }
    }

    // Then anything stuck in the input bank that this oven can never cook —
    // **what it has already worked, first**.
    //
    // Two kinds of material get stuck here and they are not equally stuck. A
    // unit at the top grade is finished as far as *anyone* is concerned; a
    // unit this oven has already touched is finished only here, and another
    // refinery would take it a grade higher. So the one worth pushing out is
    // the one that still has somewhere to go, and pushing it out is what makes
    // room for something this oven can actually use.
    //
    // Ordering, not a new rule: both were already handed on, first-fit by bank
    // position, so which one left was decided by the order they happened to
    // arrive in.
    const stuck: number[] = [];
    let coalKept = 0;
    const keepCoal = coalWanted(position, industry, sq);
    // **A bank full of metal with nothing to burn lets its newest go**, one a
    // tick, so a coal can get in — the jam `machineAccepts` now refuses to
    // build, reached by a pool lapsing or a save from before it.
    const jammed =
      DESIGN.metalNeedsFuel &&
      !isFoundryAt(position, industry, sq) &&
      bufferTotal(industry, sq, "in") >= capacityAt(position, industry, sq) &&
      metalAwaiting(position, industry, sq) > coalHeld(industry, sq) + fuelAt(industry, sq);
    let release = -1;
    if (jammed) {
      for (let i = unitCount(industry, sq, "in") - 1; i >= 0; i--) {
        const unit = unitAt(industry, sq, i, "in");
        if (fuelled(unit) && canRefine(position, industry, sq, unit)) {
          release = i;
          break;
        }
      }
    }
    for (let i = 0; i < unitCount(industry, sq, "in"); i++) {
      const unit = unitAt(industry, sq, i, "in");
      if (i === release) {
        stuck.push(i);
        continue;
      }
      // What this oven can still cook stays; what it cannot — worked here,
      // top of its line, or a special no oven may touch — moves on.
      if (canRefine(position, industry, sq, unit)) continue;
      // Its fuel stays too, as much as its metal wants (§31 M2).
      if (burnsAsFuel(unit) && coalKept < keepCoal) {
        coalKept++;
        continue;
      }
      stuck.push(i);
    }
    // Stable: worked-here before merely-finished, and within each the oldest
    // first, because two clients derive this independently (PLAN §5.5).
    stuck.sort((a, b) => {
      const workedA = unitTouchedBy(unitAt(industry, sq, a, "in"), id) ? 0 : 1;
      const workedB = unitTouchedBy(unitAt(industry, sq, b, "in"), id) ? 0 : 1;
      return workedA - workedB || a - b;
    });
    for (const i of stuck) {
      const unit = unitAt(industry, sq, i, "in");
      // Outbound for the released metal: the field still counts this oven a
      // destination for it, so a descent from here finds nowhere lower.
      if (sendOne(position, industry, sq, owner, "in", unit, i, moves, cache, i === release)) break;
    }
  }
}

/**
 * Hand one unit to the best place that will take it — a machine or a lane.
 * True if it moved.
 *
 * One path for both, because `loadTarget` already ranks them against each other
 * on the field. It used to be two: this, which refused anything that was not a
 * machine, and an `emitOntoBelt` fallback that then picked a lane by
 * `outboundBelts` order. Two answers to one question, and the second one had no
 * idea where the lane went.
 */
function sendOne(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  store: Bank,
  unit: number,
  index = 0,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[],
  cache?: DemandCache,
  /** Empty it whatever the field says about this square — an out bank always is. */
  outbound = store === "out"
): boolean {
  const target = loadTarget(position, industry, sq, owner, unit, cache, outbound);
  if (!target) return false;
  if (unitCount(industry, sq, store) <= index) return false;

  // Read before the push: on a lane it is the slot the ingot lands in, and a
  // machine's banks are drawn as one box, so -1 is its own hands.
  const slot = target.machine ? -1 : cargoTotal(industry, target.square);
  const moving = removeUnit(industry, sq, index, store);
  const landed = target.machine
    ? deliverUnit(position, industry, target.square, moving)
    : pushUnit(industry, target.square, moving, "cargo");
  if (!landed) {
    pushUnit(industry, sq, moving, store);
    return false;
  }
  moves?.push({
    from: sq,
    fromIndex: -1,
    to: target.square,
    toIndex: slot,
    phase: "handoff"
  });
  return true;
}

/**
 * Consumers take a unit off any adjacent part of theirs that is carrying one.
 *
 * Deliveries used to require a belt aimed *into* the machine, which meant a
 * lane running past a bishop was useless to it and every consumer needed its
 * own spur. Grabbing sideways makes a trunk line feed everything along it, and
 * it is the same one-unit-a-turn throughput either way.
 *
 * One unit per consumer per turn, `materialLinks` order, oldest unit first —
 * all fixed, because two clients derive this independently (PLAN §5.5).
 */
/**
 * How well a unit suits a machine; lower is better, Infinity means it cannot
 * use it at all.
 *
 * A foundry wants the grade its recipe asks for. It will take higher — grade
 * gates the tier you can mint, so steel serves a recipe that only needs alloy
 * — but it should not *prefer* to, because a foundry that swallowed the good
 * material while sitting on a recipe scrap could have filled ends up holding
 * a bank of steel and a queue of pawns it could have minted from ore.
 *
 * Everything else takes the **dearest** thing it can use. Take the best, spend
 * the worst: acquisition and spending pull in opposite directions on purpose.
 * A refinery adds a grade wherever it lands, so the turn is worth more spent
 * on silver than on iron; and a carrier that picks up the valuable unit is
 * moving the valuable unit, which is the one you wanted moved.
 *
 * Spending stays cheapest-first (`takeUnit`, and a foundry paying a recipe),
 * because there the good material is what you are trying *not* to consume.
 */
function unitPreference(
  position: Position,
  industry: IndustryState,
  machine: number,
  owner: Color,
  unit: number
): number {
  if (!acceptsUnit(position, industry, machine, owner, unit)) return Infinity;

  const piece = pieceAt(position, machine);
  if (pieceType(piece) === ROOK) {
    const target = targetAt(industry, machine) as PieceType | 0;
    // A foundry still wants *exactly* what its recipe asks for. It is
    // spending, not collecting: one that reached for the best available would
    // swallow the steel to mint a pawn and leave a queue of pieces it could
    // have made from ore. This is the case dearest-first must not eat.
    // Per bill row now: how far past the row's rung the unit is, within the
    // row's own line.
    if (target) {
      for (const row of billFor(target)) {
        if (unitMeets(unit, row.line, row.grade)) return unitGrade(unit) - row.grade;
      }
      // Holdable — a foundry is a stockpile — and wanted by no row of the
      // recipe: after every unit the bill can actually spend, however far
      // over-grade those run.
      return UNWANTED;
    }
  }
  // **Material this machine has already refined ranks last.**
  //
  // Only a king can be here: `acceptsUnit` refuses a plain bishop anything it
  // has cooked, and a king passes that test earlier as a *foundry* — it can
  // spend a bar it cannot cook, which is why it is allowed to hold one at all.
  //
  // Allowed, but not *preferred*. A king standing on a lane took the dearest
  // thing passing, which meant the bar it had just refined and sent onward was
  // the first thing it pulled back — the same failure a miner grabbing off its
  // own belt has, and the reason `isConsumer` excludes pawns. Ranked last, a
  // king takes it back only when nothing better is going by, which is the
  // honest reading of "somebody has to hold this".
  //
  // A large finite score rather than `Infinity`: it must still be *reachable*,
  // or a king with a stalled lane beside it would sit there while its own
  // output circled. See `wantedElsewhere` for the other half of the rule.
  if (refinesAt(position, machine) && unitTouchedBy(unit, refinerIdAt(industry, machine))) {
    return WORKED_HERE;
  }

  // Negated, because `bestUnitFor` takes the lowest score — and by the
  // cross-line price, since a raw rung cannot rank marble against steel.
  return -unitValue(unit);
}

/**
 * The rank a unit gets at the machine that already worked it: below anything
 * usable, above nothing at all.
 *
 * Grades score as `-grade`, so every genuinely useful unit is at most zero and
 * any positive number sorts after all of them.
 */
const WORKED_HERE = 1;

/**
 * The rank a unit gets at a foundry whose recipe has no row for it: behind
 * everything the bill can spend, however far over its rungs those run — a
 * grade distance is a small positive number, so this has to sit past any of
 * them or a cobble would beat a diamond to a queen's forge.
 */
const UNWANTED = 1000;

/** Index of the unit in `source`'s store this machine would rather have. */
function bestUnitFor(
  position: Position,
  industry: IndustryState,
  machine: number,
  owner: Color,
  source: number,
  store: Store
): number {
  let best = -1;
  let bestScore = Infinity;
  for (let i = 0; i < unitCount(industry, source, store); i++) {
    const score = unitPreference(
      position,
      industry,
      machine,
      owner,
      unitAt(industry, source, i, store)
    );
    // Strictly better, so ties keep the oldest unit — a fixed order, because
    // two clients derive this independently (PLAN §5.5).
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

/**
 * Would any machine of ours *other than this one* take this unit?
 *
 * The test behind "if nothing else wants it". Deliberately asked of the whole
 * side rather than of the neighbours: a lane exists to carry material past the
 * machines that cannot use it, so "there is a rook eight squares along that
 * needs this" is exactly the case where a king should keep its hands off.
 */
function wantedElsewhere(
  position: Position,
  industry: IndustryState,
  owner: Color,
  from: number,
  unit: number
): boolean {
  for (const sq of liveSquares(position.board)) {
    if (sq === from) continue;
    const piece = pieceAt(position, sq);
    if (!piece || pieceColor(piece) !== owner) continue;
    if (!consumesAt(position, sq)) continue;
    if (acceptsUnit(position, industry, sq, owner, unit)) return true;
  }
  return false;
}

export function grabFromAdjacent(
  position: Position,
  industry: IndustryState,
  owner: Color,
  /** Optional: what moved, so the renderer can animate the pass. */
  moves?: CargoMove[]
): void {
  for (const sq of liveSquares(position.board)) {
    const piece = pieceAt(position, sq);
    if (pieceColor(piece) !== owner) continue;
    // Consumers only. Pawns and queens have buffers too, but they *supply*
    // lanes — letting them grab meant a miner pulled its own ore straight back
    // off the belt it had just loaded, and nothing ever left the square.
    if (!consumesAt(position, sq)) continue;
    if (!machineAccepts(position, industry, sq, owner)) continue;

    // **The square underneath first**, then the neighbours.
    //
    // A piece standing on its own part shares the square: the part is squashed
    // flat and the lane keeps running beneath it. So cargo arrives under a
    // foundry, and the foundry could reach a lane one square away but not the
    // one it was standing on — it watched its own material ride past its feet.
    // Reported as a rook that would not fill from a belt it was sitting on,
    // and it is the same rule as the neighbours: a lane you are on is at least
    // as reachable as one you are beside.
    for (const adj of [sq, ...materialLinks(position, industry, sq)]) {
      // Your *own* lanes, and deliberately not an ally's. Depositing onto a
      // friend's belt is help; reaching into it and taking what is on it is
      // the same rule read backwards, and it is theft. A split army feeds your
      // economy — it does not get to live off it.
      if (!beltIsLive(industry, adj, owner)) continue;
      if (unitCount(industry, adj, "cargo") === 0) continue;

      // Take what this machine can actually use, and of those the one it
      // wants most: a refinery will not pull back something it has already
      // cooked, and a foundry reaches past the steel for the ore its current
      // recipe is actually asking for.
      const index = bestUnitFor(position, industry, sq, owner, adj, "cargo");
      if (index < 0) continue;
      const taking = unitAt(industry, adj, index, "cargo");
      // **Its own work, only when nothing else wants it.**
      //
      // A king is the one machine that will hold material it has already
      // refined — it can spend what it cannot cook. That makes it a useful
      // last resort and a terrible first one: a king beside a trunk line
      // swallowed the bar it had just sent out, and the lane past it starved
      // while a rook downstream waited for exactly that unit.
      if (
        refinesAt(position, sq) &&
        unitTouchedBy(taking, refinerIdAt(industry, sq)) &&
        wantedElsewhere(position, industry, owner, sq, taking)
      ) {
        continue;
      }
      if (!pushUnit(industry, sq, taking, "in")) break;
      removeUnit(industry, adj, index, "cargo");
      // Off the lane and into the machine's hands: an index of -1 is the
      // square's own box rather than a slot in a pile.
      moves?.push({ from: adj, fromIndex: index, to: sq, toIndex: -1, phase: "grab" });
      break;
    }
  }
}

/**
 * What a king is doing this turn.
 *
 * A king mines, refines or forges — one of the three, never all of them. It
 * is the only piece that can do every industrial job, and letting it do them
 * all at once would make standing it anywhere useful strictly better than
 * anything else on the board, which is a strange thing to say about the piece
 * you are also trying not to lose.
 *
 * An explicit order is absolute, even when there is nothing to do with it:
 * the tempo builds while the king waits, so posting a king to the ovens
 * before the ore arrives is a real thing to do with a turn. With no order,
 * fall to whatever is to hand, in the order a king in the field would — dig
 * what you are standing on, cook what you are carrying, build if you can pay.
 */
export function kingTask(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): number {
  const chosen = kingTaskAt(industry, sq);
  if (chosen >= 0) return chosen;

  // What it can *actually do*. Two jobs to choose between now that the king
  // does not dig — cook what you are holding, or build if you can pay — and
  // refining is the default because it is the one that feeds the other.
  if (
    isCooking(industry, sq) ||
    refinableIndex(position, industry, sq, refinerIdAt(industry, sq), fuelable(industry, sq)) >= 0
  ) {
    return TASK_REFINE;
  }
  const target = targetAt(industry, sq);
  if (target && canForge(position, industry, sq, owner, target as PieceType) === null) {
    return TASK_FORGE;
  }
  return TASK_REFINE;
}

/**
 * This turn's capacity for a king's current job, and the bookkeeping that
 * grows it.
 *
 * Tempo is "how many turns has this king been left on this job", which is
 * also how much of that job it gets through this turn. Switching resets it to
 * one. Kept as a count of turns rather than a rate so the rule a player has
 * to hold is one sentence: leave it alone and it speeds up.
 */
export function kingCapacity(industry: IndustryState, sq: number): number {
  return Math.min(RULES.kingTempoMax, Math.max(1, kingTempoAt(industry, sq)));
}

/**
 * Note that a king spent its turn on `task`, and move its tempo accordingly.
 *
 * Called once per king per turn from the machine pass, whether or not the job
 * actually found work — a king posted to the ovens with nothing to cook is
 * still on the ovens, and spinning up is the point of posting it early.
 */
export function noteKingTurn(industry: IndustryState, sq: number, task: number): void {
  // Once a turn, whichever job got there first. Without this the tempo would
  // step twice on a turn where the king both dug and passed through the
  // machine pass, and "one job a turn" would be worth two.
  if (kingActedAtOn(industry, sq) === industry.turnCounter) return;

  const previous = industry.kingLastTask.get(sq);
  if (previous === task) {
    setKingTempo(industry, sq, kingTempoAt(industry, sq) + 1);
  } else {
    setKingTempo(industry, sq, 1);
    industry.kingLastTask.set(sq, task);
  }
  setKingActedAt(industry, sq, industry.turnCounter);
}

export function runRefineries(
  position: Position,
  industry: IndustryState,
  owner: Color
,
  /** Optional: what the ovens did, for the renderer. */
  moves?: CargoMove[],
  /**
   * Machines that already spent this tick — a Miracle Healer who found somebody
   * to mend (`runHealers`). Scaffolding for one tick, `handedOff`'s shape:
   * "she cooks when there is nobody to heal" is one decision, and only the pass
   * that healed knows which way it went.
   */
  busy?: ReadonlySet<number>,
  /**
   * The last tick of its owner's turn (§31 M2): no coal is burned, since the
   * point would vanish at the turn's start before any tick could spend it.
   */
  finalTick = false,
  /** Out: how many coal were burned — progress, for the Gambit. */
  burned?: { count: number },
  /** Out: how many of the units cooked were metal — a report, read by the tuner. */
  metal?: { count: number }
): number {
  let cooked = 0;

  for (const sq of liveSquares(position.board)) {
    const piece = pieceAt(position, sq);
    if (pieceColor(piece) !== owner || !refinesAt(position, sq)) continue;
    if (busy?.has(sq)) continue;

    const king = pieceType(piece) === KING;
    // A king does one job a turn, and the bookkeeping for which one happens
    // here because this is the pass every king passes through — mining is a
    // player action that may never be pressed, and forging may never be
    // ordered, but this runs for every refinery every turn.
    let capacity: number = RULES.refineryRate;
    if (king) {
      // Already dug or forged this turn: that was the king's one job.
      if (kingActedAtOn(industry, sq) === industry.turnCounter) continue;
      const task = kingTask(position, industry, sq, owner);

      // Book the turn only when the king actually *works* it.
      //
      // This used to book unconditionally, so a king with nothing to do —
      // heap gone, oven empty, nothing to cook — had its tempo climb anyway.
      // Tempo is what a king earns by being left on a job, and earning it for
      // standing still made the whole ramp a waiting game rather than a
      // commitment: the reported symptom was the mining counter ticking up on
      // an idle king.
      //
      // An *explicit* posting is the deliberate exception and stays absolute:
      // you told it to wait at the ovens, so the tempo builds while it waits
      // and posting a king before the ore arrives is a real move. Only the
      // inferred case has to earn it.
      const posted = kingTaskAt(industry, sq) >= 0;
      const working =
        task === TASK_REFINE &&
        (isCooking(industry, sq) ||
          refinableIndex(position, industry, sq, refinerIdFor(industry, sq), fuelable(industry, sq)) >= 0);
      if (posted || working) noteKingTurn(industry, sq, task);

      if (task !== TASK_REFINE) continue;
      capacity = kingCapacity(industry, sq);
    }

    const id = refinerIdFor(industry, sq);

    // 1. Pull finished batches into the output buffer, oldest first, as far as
    //    capacity and the output bank allow.
    for (let i = 0; i < capacity; i++) {
      if (!isCooking(industry, sq)) break;
      if (bufferTotal(industry, sq, "out") >= RULES.refineryOutputBuffer) break;
      const done = shiftCooking(industry, sq);
      if (metal && unitLine(done) === METAL) metal.count++;
      pushUnit(industry, sq, done, "out");
      // Same square both ends: the oven and the banks are drawn as one box, so
      // this is a pulse rather than a journey.
      moves?.push({ from: sq, fromIndex: -1, to: sq, toIndex: -1, phase: "cook" });
      cooked++;
    }

    // 2. Load the oven from the INPUT side, one grade higher. Taking from the
    //    output side here would let the refinery cook its own product forever.
    //
    //    It also skips anything this refinery has already handled: a unit that
    //    went A -> B may not come back to A. Without that, two refineries could
    //    ping-pong one unit to the top grade with no third machine involved,
    //    and the grade ladder would stop meaning "this passed through three
    //    different hands".
    //
    //    Emptying before filling is what keeps the one-turn cook honest at any
    //    capacity: the loop above only ever takes batches that were already in
    //    the queue, so a unit loaded here is behind them and cannot come out
    //    until next turn however fast the king is working.
    // **Fuel (§31 M2).** Raising metal a rung spends a point from this
    // machine's pool; with none, a refiner holding coal burns one for a point,
    // and **the burn is the whole tick** — so it only happens before anything
    // is loaded, and never on its turn's last tick (the point would vanish).
    // Stone needs none. Metal is therefore two ticks a rung on its own coal.
    let loaded = 0;
    for (let i = 0; i < capacity; i++) {
      if (cookingCount(industry, sq) >= capacity) break;
      const pool = fuelAt(industry, sq);
      const canBurn = loaded === 0 && !finalTick && coalHeld(industry, sq) > 0;
      // **Metal first whenever it can be fuelled** (2026-10-29): the coal was
      // taken to cook it, and a point unspent dies at the turn's start. Stone
      // outbid iron on price alone and the coal sat idle. Then the dearest
      // fuel-free unit, as before.
      //
      // Peeked before it is taken. `refinableIndex` returns the *dearest*
      // refinable unit, so a grade-0 answer means grade 0 is all there is —
      // breaking here gives up nothing else the oven could have cooked.
      const metal = pool > 0 || canBurn ? refinableIndex(position, industry, sq, id, fuelled) : -1;
      const next = metal >= 0 ? metal : refinableIndex(position, industry, sq, id, (unit) => !fuelled(unit));
      if (next < 0) break;
      const peek = unitAt(industry, sq, next, "in");
      const burns = fuelled(peek);
      if (burns && pool === 0) {
        // `allow` let it through on `canBurn`: burn, and the tick is spent.
        takeUnitOf(industry, sq, FUEL, COAL);
        setFuel(industry, sq, 1);
        if (burned) burned.count++;
        moves?.push({ from: sq, fromIndex: -1, to: sq, toIndex: -1, phase: "cook" });
        break;
      }
      const unit = removeUnit(industry, sq, next, "in");
      if (burns) setFuel(industry, sq, pool - 1);
      const top = lineTop(unitLine(unit));
      const grade = Math.min(top, unitGrade(unit) + 1);
      pushCooking(industry, sq, unitRefinedBy(unit, id, grade));
      moves?.push({ from: sq, fromIndex: -1, to: sq, toIndex: -1, phase: "cook" });
      loaded++;
    }
  }

  return cooked;
}

export interface MintResult {
  square: number;
  type: PieceType;
  at: number;
}

/**
 * Where a minted piece appears: the first empty square one space from the
 * rook, orthogonals before diagonals so a piece comes out of the foundry's
 * side rather than its corner.
 *
 * The order is fixed and explicit because two networked clients must agree
 * (PLAN §5.5) — never iterate a Set or Map to decide this.
 */
const MINT_ORDER = [...ROOK_DIRS, ...BISHOP_DIRS] as const;

export function mintSquare(position: Position, rook: number): number {
  return forgeSquares(position, rook)[0] ?? -1;
}

/** Every empty square touching this foundry, in a fixed order. */
export function forgeSquares(position: Position, rook: number): number[] {
  const out: number[] = [];
  for (const offset of MINT_ORDER) {
    const sq = rook + offset;
    if (forgeReaches(position, rook, sq)) out.push(sq);
  }
  return out;
}

/**
 * Could this foundry put a piece down on that one square?
 *
 * `forgeSquares` asked square by square, for the callers that already have a
 * square in hand — co-funding asks it of every foundry touching a landing
 * square, and building the whole list to look for one member of it was the
 * expensive half of that (`coalition.ts`). One definition, so a square the
 * list offers and a square this accepts cannot disagree; the list is written
 * in terms of this.
 *
 * **Which is why adjacency is asked here and not left to the caller.** Every
 * caller inside this file hands it an offset from the foundry and could not
 * fail it — but `canForgeAt` is exported and is the square-shaped question the
 * interface asks, so without this it answered "yes, that is legal" for a
 * foundry and a square two apart (some *other* foundry beside the square pays,
 * and the lead's own reach was never tested) and the reducer then threw. A
 * legality function that says yes to a command the reducer refuses is exactly
 * the disagreement this pair of functions exists to prevent.
 *
 * `touches` is the reading of "beside" that territory, a knight's re-aim and
 * §26 A1 all use — eight-way, and not a fifth definition of it.
 */
export function forgeReaches(position: Position, foundry: number, at: number): boolean {
  if (!touches(foundry, at)) return false;
  if (!hasCell(position, at) || pieceAt(position, at) !== 0) return false;
  // Nor onto a building's square, of any kind (§31 S1): a mint is born on
  // bare floor — or on a belt part of its own side's, which it stands on (S2).
  if (!noBuildingFor(position, at, pieceColor(pieceAt(position, foundry)))) return false;
  // **Not onto a wall**: a piece that *appears* there is standing on a moat
  // no piece could have walked onto (§26 C13). Here rather than in the
  // reducer's case, because this is the list the case validates `at` against
  // and the list the planner picks from: one definition, so the opponent
  // cannot propose a square the rules refuse.
  if (moatLeft(position, at) > 0) return false;
  return true;
}

/**
 * Units this foundry could put toward `type`.
 *
 * Material converts **down** one-for-one: a unit of steel pays for a recipe
 * that only asks for ore, and pays for exactly one unit of it. Refining past
 * what you needed is therefore never a mistake that strands material — the
 * grade ladder decides which *tiers* you may build, never whether you can
 * spend what you are holding.
 */
export function usableFor(
  position: Position,
  industry: IndustryState,
  rook: number,
  type: PieceType
): number {
  const row = billFor(type)[0];
  if (!row) return 0;
  return bufferAtLeast(industry, rook, row.grade as Grade, "in", row.line);
}

/**
 * Can this square's banks cover every row of the bill? Rows are independent —
 * a bill carries at most one row per line, so nothing is counted twice.
 */
export function canPayBill(industry: IndustryState, sq: number, bill: Bill): boolean {
  return bill.every(
    (row) => bufferAtLeast(industry, sq, row.grade as Grade, "in", row.line) >= row.count
  );
}

/** Pay the bill: each row spent cheapest-by-value within its own line. */
export function spendBill(industry: IndustryState, sq: number, bill: Bill): void {
  for (const row of bill) {
    for (let i = 0; i < row.count; i++) takeUnitOf(industry, sq, row.line, row.grade);
  }
}

export type ForgeError =
  | "not-your-foundry"
  | "not-mintable"
  | "too-poor"
  | "no-room"
  | "king-busy"
  // The two a *square* can answer and a foundry cannot, since the bill is
  // split (`coalition.ts`): nobody of yours touches that square at all, and
  // the foundries that do cannot pay for it between them.
  | "no-foundry"
  | "no-coalition";

/**
 * What this side may forge (PLAN §15.4's **roles** seam). Every army the same
 * since §31 R2; kept as the seam a faction's recipe book hangs on.
 */
export function mintableFor(_position: Position, _side: Color): readonly PieceType[] {
  return MINTABLE;
}

/** Why this foundry cannot forge `type` right now, or null. */
export function canForge(
  position: Position,
  industry: IndustryState,
  rook: number,
  owner: Color,
  type: PieceType
): ForgeError | null {
  const piece = pieceAt(position, rook);
  if (!piece || pieceColor(piece) !== owner || !isFoundryAt(position, industry, rook)) {
    return "not-your-foundry";
  }
  if (!mintableFor(position, owner).includes(type)) return "not-mintable";
  // A king forges instead of mining or refining, not as well as.
  if (pieceType(piece) === KING && kingBusyForForge(industry, rook)) return "king-busy";
  // **Affordability is a question about a square now, not about a hopper.**
  // The bill is split between every foundry touching where the piece lands
  // (PLAN-UI §28.4), so "can this foundry forge" is really "is there a square
  // beside it where a forge it leads could be paid for" — which is what the
  // shelf's recipe list, `pieceBadges` and `forgeCandidates` have always meant
  // by it. A lone foundry is its own coalition of one, so the classic answer
  // is unmoved.
  if (forgeSquares(position, rook).length === 0) return "no-room";
  if (fundableSquares(position, industry, rook, owner, type).length === 0) return "too-poor";
  return null;
}

/** Spend the material and put the piece down. The caller checks legality. */
/**
 * Which way a minted pawn points when its foundry was not told.
 *
 * A king points it *away from himself* — the pawn is put down beside him, and
 * the direction it came from is the one direction it demonstrably should not
 * face. It is also the reading that makes a king minting a screen of pawns do
 * something sensible without being told four times.
 *
 * A rook keeps the side's forward. A rook can be anywhere and its own square
 * says nothing about which way the war is; "the way your army marches" is the
 * only answer that does not need a second question.
 *
 * Either way this is only the *default*, and an explicit facing wins — a
 * foundry's stored `mintFacing` is set by its owner precisely so this does not
 * have to guess right.
 */
function defaultMintFacing(
  position: Position,
  foundry: number,
  at: number,
  owner: Color
): Direction {
  // **Away from whatever made it, whichever foundry that is.**
  //
  // This used to differ by piece: a king pointed a pawn away from himself, and
  // a rook used the side's forward on the argument that a rook can be anywhere
  // and its own square says nothing about which way the war is. True, and
  // beside the point — the *pawn* is put down touching the foundry, so the one
  // direction it demonstrably should not face is the one it came from, and a
  // rook minting to its south handed you a pawn looking back over the rook.
  // Reported from play, which is the only place the difference shows.
  //
  // The pawn lands on a square touching the foundry, so the step between them
  // is exactly one of the eight neighbours. Cardinals answer directly; a
  // diagonal has no facing of its own, so it takes the file component when
  // there is one, which points the pawn out along the rank rather than back
  // across the piece that made it.
  const df = Math.sign(fileOf(at) - fileOf(foundry));
  const dr = Math.sign(rankOf(at) - rankOf(foundry));
  if (df > 0) return EAST;
  if (df < 0) return WEST;
  if (dr > 0) return NORTH;
  if (dr < 0) return SOUTH;
  return forwardFacing(owner);
}

export function forgeOnce(
  position: Position,
  industry: IndustryState,
  rook: number,
  owner: Color,
  type: PieceType,
  at: number,
  facing?: Direction
): void {
  // **Who pays is decided before anything is put down**, because the coalition
  // is the foundries touching an *empty* square and placing the piece fills it.
  const bill = billFor(type);
  const members = coalitionFor(position, industry, at, owner, type) ?? [rook];
  const paid = payCoalition(industry, members, bill, warehousesFor(industry, owner, at));

  // A pawn forged onto a promotion square is promoted the moment it lands.
  //
  // The square asks "what does a pawn become here", and it does not care how
  // the pawn arrived — walking one over and minting one on the spot are the
  // same question. Without this the square is a place you can build a pawn and
  // then have to move it off and back on, which is a rule nobody would guess.
  // The enemy back rank counts as well as a promotion square.
  //
  // `promotesOn` is the one place that answers "does a pawn arriving here
  // become something else", and it knows about both — the classic rank while
  // the classic board lasts, and the squares a grown board reveals. This asked
  // only about promotion squares, so a rook minting a pawn onto the eighth
  // rank produced a pawn that could never move again: it had nowhere to step
  // and no rank left to earn. The square asks "what does a pawn become here"
  // and does not care how the pawn arrived.
  const promotes = type === PAWN && promotesOn(position, owner, at);
  setPiece(position, at, makePiece(owner, promotes ? QUEEN : type));
  // **A king is an ordinary bill now** (§31 M4), and a royal is booked where
  // every royal is: `addKing`, or check and mate cannot see it.
  if (type === KING) addKing(position, owner, at);
  if (promotes) {
    // Spent by being used, and gone: a promotion square is a one-off, not a
    // fixture you can queue pawns through. A back-rank promotion has no use
    // to spend, and `setPromotionUses(..., 0)` on a square that had none is
    // already a no-op, so this needs no second branch.
    setPromotionUses(position, at, 0);
  } else if (type === PAWN) {
    setFacing(position, at, facing ?? defaultMintFacing(position, rook, at, owner));
  }
  // **Every king that actually paid has done its one job this turn**, and a
  // king that paid nothing has not — including the lead, which may be nothing
  // but the square the pawn is pointed away from. A king marked busy for a
  // bill its neighbour footed would be losing a mining turn to somebody else's
  // forge.
  for (let i = 0; i < members.length; i++) {
    if (paid[i] === 0) continue;
    if (pieceType(pieceAt(position, members[i])) === KING) {
      noteKingTurn(industry, members[i], TASK_FORGE);
    }
  }
}
